/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2025-01-22 08:02:49
 **/

import { create } from "zustand/react";
import { createJSONStorage, persist, subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

/**
 * Represents the state configuration object used for handling application settings and hydration states.
 *
 * Properties:
 * - `configure`: An object containing a record of configuration settings.
 * - `has_hydrated`: A boolean value indicating whether the state has been hydrated.
 * - `set_hydrated`: A function to set the hydration state.
 */
type ConfigureState = {
	configure: Record<string, unknown>;
	has_hydrated: boolean;
	set_hydrated: (value: boolean) => void;
};

/**
 * The `useConfigure` variable is a state management hook designed to handle configuration-related states.
 * It leverages Zustand with persistence support to store and retrieve state data from a storage backend such as `localStorage`.
 *
 * The `useConfigure` state includes the following:
 * - `configure`: An object to store configuration-related data.
 * - `has_hydrated`: A boolean indicating whether the state has been successfully hydrated from storage.
 * - `set_hydrated`: A function to update the `has_hydrated` value.
 *
 * The state is persisted with:
 * - `name`: The unique name for the persisted state ("persist").
 * - `partialize`: A function specifying which parts of the state are persisted (`configure` only).
 * - `storage`: Specifies the storage backend, with `localStorage` configured through `createJSONStorage`.
 * - `onRehydrateStorage`: A callback triggered during rehydration to set `has_hydrated` to `true` after hydration completes.
 */
const useConfigure = create(
	persist(
		subscribeWithSelector(
			immer<ConfigureState>((set) => ({
				configure: {},
				has_hydrated: false,
				set_hydrated: (value: boolean) => {
					set({ has_hydrated: value });
				}
			}))
		),
		{
			name: "config",
			partialize: (state) => ({
				configure: state.configure
			}),
			storage: createJSONStorage(() => localStorage),
			onRehydrateStorage: (state) => {
				return () => state.set_hydrated(true);
			}
		}
	)
);
export default useConfigure;
